'use client';

import { createContext, useContext, useState, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { nanoid } from 'nanoid';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalNotifyOptions {
    message: ReactNode;
    onClose?: () => void;
}

interface ModalState {
    options: ModalNotifyOptions;
    id: string;
}

interface ModalContext {
    notify(options: ModalNotifyOptions): void;
    remove(): void;
}

const Context = createContext({
    notify: () => undefined,
    remove: () => undefined,
} as ModalContext);

const dialogID = 'modal-dialog';
const xButtonID = 'modal-x-button';

/**
 * A context provider that supports a `useModal()` hook. The `useModal` hook provides a `notify`
 * method which uses components from DaisyUI to show a modal. It also provides a `remove` method for
 * removing the modal. Only one modal can be shown at a time.
 */
export const ModalProvider: FC<{
    children: ReactNode;
}> = ({ children }) => {
    const [modal, setModal] = useState<ModalState | null>(null);
    const onCloseRef = useRef<() => void>(null);

    const notify = (options: ModalNotifyOptions, id = nanoid()) => {
        setModal({ options, id });

        onCloseRef.current = options?.onClose ?? null;

        const dialogElement = document.getElementById(
            dialogID,
        ) as unknown as HTMLDialogElement | null;

        if (dialogElement) {
            dialogElement.showModal();
        } else {
            throw new Error('Could not find dialog element by id');
        }
    };

    const remove = () => {
        const closeButton = document.getElementById(
            xButtonID,
        ) as unknown as HTMLButtonElement | null;

        if (closeButton) {
            closeButton.click();
            setModal(null);
            if (onCloseRef.current) {
                onCloseRef.current();
            }
        } else {
            throw new Error('Could not find X button by id');
        }
    };

    return (
        <Context.Provider
            value={{
                notify,
                remove,
            }}
        >
            {children}
            <dialog
                id={dialogID}
                className="modal"
                onClose={() => {
                    setModal(null);
                    if (onCloseRef.current) {
                        onCloseRef.current();
                    }
                }}
            >
                <div className="modal-box max-h-[80vh] min-w-80 max-w-[80vw] overflow-hidden rounded-lg bg-white p-4 dark:bg-gray-800">
                    <form method="dialog">
                        <button
                            id={xButtonID}
                            className="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
                            aria-label="Close"
                            style={{
                                zIndex: 1000,
                            }}
                        >
                            <XMarkIcon />
                        </button>
                    </form>
                    {modal?.options?.message}
                </div>
            </dialog>
        </Context.Provider>
    );
};

type UseModal = () => ModalContext;

export const useModal: UseModal = () => useContext(Context);
